import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../utils/axios';
import { 
    Container, 
    Typography, 
    Box, 
    Chip, 
    Paper,
    Skeleton,
    Divider,
    Alert,
    CircularProgress
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const ArticleDetail = () => {
    const { id } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchArticle();
    }, [id]);

    const fetchArticle = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`/api/articles/${id}`);
            setArticle(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Error fetching article');
            console.error('Error fetching article:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Skeleton variant="text" height={40} />
                <Skeleton variant="text" height={24} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={300} sx={{ mb: 2 }} />
                <Skeleton variant="text" height={24} count={5} />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    if (!article) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="warning">
                    Article not found
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={0} sx={{ p: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {article.title}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                            {new Date(article.createdAt).toLocaleDateString()}
                        </Typography>
                    </Box>
                    <Chip 
                        label={article.category} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                    />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box 
                    sx={{ 
                        '& h2': { mt: 4, mb: 2 },
                        '& p': { mb: 2 },
                        '& ul, & ol': { pl: 4, mb: 2 },
                        '& li': { mb: 1 }
                    }}
                    dangerouslySetInnerHTML={{ __html: article.content }}
                />
            </Paper>
        </Container>
    );
};

export default ArticleDetail; 